/**
 * SaleDetailPresenter
 * --
 */
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import {
  Content,
  Row,
  Col,
  Title,
  InputFormTable,
  Text,
  ModalLayout,
  Thumbnail,
} from 'components';
import {
  Input,
  InputNumber,
  Button,
  Flex,
  Select,
  Table,
  Checkbox,
  Radio,
  DatePicker,
  Spin,
  Space,
  Upload,
  Empty,
  Alert,
  Tabs,
} from 'antd';
import {
  MinusOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import Formatter from 'utils/Formatter';
import MessageAlert, { stringToMoneyFormat } from 'utils';
import { ORDER_ROUTE } from 'constants/OrderConstants';
import Payment from 'constants/Payment';
const MENU_TYPES = {
  DESSERT: { label: '디저트', value: 'DESSERT' },
  MD: { label: 'MD', value: 'MD' },
  GENERAL: { label: '일반', value: 'GENERAL' },
  ALC: { label: '주류', value: 'ALC' },
  ETC: { label: '기타', value: 'ETC' },
};
const SaleRegisterPresenter = (props) => {
  const {
    auth = 'ADMIN',
    searchInput = null,
    menuList = [],
    userData = null,
    shopList = [],
    categoryList = [],
    //
    onChange = () => {},
    onAmountChange = () => {},
    onOptionToggle = () => {},
    onChoiceSelect = () => {},
    onMenuComplete = () => {},
    selectedList = [],
    onSelect = () => {},
    modalType = 'MENU',
    onModalType = () => {},
    isOpen = false,
    onIsOpen = () => {},
    amountList = [],
    onAmountList = () => {},
    onSubmit = () => {},
    //
    isLoading = false,
    inputValue = null,
    selectedUserPhone = null,
    onSelectedUserPhone = () => {},
    uploadData = null,
    onUploadData = () => {},
  } = props;
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */

  /* ===== FUNCTION ===== */
  /**
   * 테이블 검색 함수
   * @param {*} selectedKeys
   * @param {*} confirm
   * @param {*} dataIndex
   */
  const handleTableSearch = (confirm) => {
    confirm();
  };
  const handleTableReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`검색어를 입력해주세요`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleTableSearch(confirm)}
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleTableSearch(confirm)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            검색
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleTableReset(clearFilters, confirm)
            }
            size="small"
            style={{
              width: 90,
            }}
          >
            초기화
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1677ff' : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ?.toString()
        ?.toLowerCase()
        ?.includes(value.toLowerCase()),
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    },
    render: (text) => text,
  });
  /**
   * 파일 업로드
   * --
   * @param {*} file
   */
  const handleFile = async (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const wsname = workbook.SheetNames[0];
      const ws = workbook.Sheets[wsname];
      const dataParse = XLSX.utils.sheet_to_json(ws);

      // 데이터 추출
      const result = dataParse
        .map((item) => {
          const newItem = {
            payment_serial_number: item['영수증번호'],
            user_phone: item['회원연락처'],
            is_mapping: true,
          };
          return newItem;
        })
        ?.filter((item) => item.payment_serial_number && item.user_phone);
      onUploadData(result);
      MessageAlert.success(`파일이 업로드 되었습니다.`);
    };

    reader.onerror = (error) => {
      MessageAlert.error('파일을 읽는 중 오류가 발생했습니다.');
      console.error('File reading error:', error);
    };

    reader.readAsArrayBuffer(file); // 실제 파일 객체를 읽음
  };

  /**
   * 엑셀 양식 다운로드
   * --
   */
  const handleGetExcelTemplate = () => {
    try {
      const warningMessage = [
        '* 영수증번호 및 회원연락처가 둘중에 하나라도 없을 시 연결되지 않습니다.',
        '* 엑셀 파일 내에 동일한 영수증 번호를 가진 회원 연락처가 여러개 있을 경우, 먼저 입력된 항목만 연결됩니다.',
        '* 엑셀 파일을 업로드하기 전에, 공백이 없는지와 연락처가 올바른 형식인지 확인해 주세요.',
      ];
      const xlsxData = [
        {
          ['영수증번호']: 'ex. 2-20250522-01-0032',
          ['회원연락처']: 'ex. 010-0000-0000',
          ['주의사항']: warningMessage[0],
        },
        {
          ['영수증번호']: '',
          ['회원연락처']: '',
          ['주의사항']: warningMessage[1],
        },
        {
          ['영수증번호']: '',
          ['회원연락처']: '',
          ['주의사항']: warningMessage[2],
        },
      ];

      const ws = XLSX.utils.json_to_sheet(xlsxData);
      // 열 너비 설정 (optional)
      ws['!cols'] = [{ wch: 25 }, { wch: 25 }, { wch: 80 }];
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(
        wb,
        `주문장_연결_양식_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`
      );
    } catch (e) {
      console.log(e);
      MessageAlert.error('엑셀을 다운로드 할 수 없습니다.');
    }
  };

  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    subTitle: {
      fontSize: 17,
      fontWeight: 500,
      lineHeight: '141%',
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 120,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },
  };
  //NOTE: 주문장 생성
  const form = [
    {
      label: '회원 선택',
      form: [
        {
          input: (
            <Flex vertical gap={10}>
              <Flex gap={2} align="center">
                <Input
                  size="large"
                  placeholder="회원 번호 입력"
                  style={{ ...styles.form.input.large, width: 300 }}
                  value={inputValue.user_phone}
                  onChange={(e) =>
                    onChange('user_phone', Formatter.hyphen(e.target.value))
                  }
                  count={{
                    show: true,
                    max: 13,
                  }}
                />
                <Flex gap={10} align="center">
                  <Button
                    onClick={() => onSelectedUserPhone(inputValue.user_phone)}
                  >
                    조회하기
                  </Button>
                  <Button
                    danger
                    onClick={() => {
                      onChange('user_phone', null);
                      onSelectedUserPhone(null);
                    }}
                  >
                    초기화
                  </Button>
                  <Text size={13} color={'#D33444'}>
                    * 입력한 번호가 없으면 비회원, 있을 경우에는 해당 번호로
                    주문장이 생성됩니다.
                  </Text>
                </Flex>
              </Flex>
              <Flex gap={10}>
                {selectedUserPhone ? (
                  <>
                    <Text>
                      조회된 정보: {'['}회원명:{' '}
                      {userData
                        ? userData?.user_name || '비회원'
                        : '회원정보 없음'}
                      , 회원 연락처:{' '}
                      {userData?.user_phone || selectedUserPhone || '-'}
                      {']'}
                    </Text>
                  </>
                ) : null}
              </Flex>
            </Flex>
          ),
        },
      ],
    },
    {
      label: '매장 선택',
      form: [
        {
          input: (
            <Select
              placeholder="매장 선택"
              size="large"
              style={{ width: 300 }}
              value={inputValue?.shop_information_id}
              disabled={auth !== 'ADMIN'}
              options={shopList?.map((shop) => ({
                label: shop?.shop_information_name,
                value: shop?.shop_information_id,
              }))}
              onChange={(e) => {
                onSelect('RESET');
                onModalType('MENU');
                onChange('shop_information_id', e);
              }}
            ></Select>
          ),
        },
      ],
      required: true,
    },
    {
      label: '메뉴 선택',
      form: [
        {
          input: (
            <Button
              disabled={menuList?.length === 0}
              onClick={() => {
                onModalType('MENU');
                onIsOpen(true);
              }}
            >
              메뉴 선택 하기
            </Button>
          ),
        },
      ],
    },
    ...(inputValue?.product_information?.length
      ? [
          {
            label: '메뉴 정보',
            form: [
              {
                input: (
                  <Flex justify="space-between" align="center" width="100%">
                    <Flex gap={10} vertical>
                      {inputValue?.product_information?.map((menu) => {
                        return (
                          <Flex
                            gap={20}
                            style={{
                              paddingBottom: '3px',
                              borderBottom: '2px solid #E5E5E5',
                            }}
                            align="center"
                          >
                            <Thumbnail src={menu?.thumbnail} />
                            <p>{menu?.menu_name}</p>
                            <Flex vertical gap={3}>
                              <p>- 온도 : {menu?.temperature_option}</p>
                              {menu?.menu_option?.map((item) => (
                                <Flex gap={5}>
                                  - {item?.option_title}
                                  <Flex vertical>
                                    {item?.option?.map((option) => {
                                      return (
                                        <Flex gap={5}>
                                          <p>
                                            {' '}
                                            - {option?.option_name || '-'} /
                                          </p>
                                          <p>
                                            <>
                                              {stringToMoneyFormat(
                                                option?.option_price || 0,
                                                '원'
                                              )}{' '}
                                              *{' '}
                                              {stringToMoneyFormat(
                                                option?.count,
                                                '개'
                                              )}
                                            </>{' '}
                                            =
                                          </p>
                                          <p>
                                            {stringToMoneyFormat(
                                              item?.option?.reduce(
                                                (acc, cur) =>
                                                  acc +
                                                  cur.option_price * cur.count,
                                                0
                                              ),
                                              '원'
                                            )}
                                          </p>
                                        </Flex>
                                      );
                                    })}
                                  </Flex>
                                </Flex>
                              ))}
                            </Flex>
                            <Flex vertical gap={3} fontSize={15}>
                              <p>
                                메뉴 금액:{' '}
                                {stringToMoneyFormat(menu?.price, '원')}
                              </p>{' '}
                              <p>
                                옵션 금액:{' '}
                                {stringToMoneyFormat(
                                  menu?.total_option_price,
                                  '원'
                                )}
                              </p>
                              <p>
                                메뉴 수량:{' '}
                                {stringToMoneyFormat(menu?.num, '개')}
                              </p>{' '}
                              <p>
                                총 합계:{' '}
                                {stringToMoneyFormat(menu?.total_price, '원')}
                              </p>{' '}
                            </Flex>
                          </Flex>
                        );
                      })}
                    </Flex>
                    <Button
                      type="primary"
                      onClick={() => {
                        onModalType('AMOUNT');
                        onIsOpen(true);
                      }}
                    >
                      금액수정
                    </Button>
                  </Flex>
                ),
                fullscreen: true,
              },
            ],

            required: true,
          },
        ]
      : []),
    {
      label: '결제 금액',
      form: [
        {
          input: (
            <InputNumber
              size="middle"
              placeholder="결제금액"
              style={{ ...styles.form.input.large, width: 300 }}
              value={inputValue?.final_price}
              addonAfter="원"
              min={0}
              onChange={(e) => onChange('final_price', e)}
            />
          ),
        },
      ],
      required: true,
    },
    {
      label: '할인 금액',
      form: [
        {
          input: (
            <Flex gap={5} align="center">
              <InputNumber
                size="middle"
                placeholder="할인금"
                style={{ ...styles.form.input.large, width: 300 }}
                value={inputValue?.final_discount}
                addonAfter="원"
                min={0}
                onChange={(e) => onChange('final_discount', e)}
              />
              <Text size={13} color={'#D33444'}>
                * 할인금 입력시 결제금액이 자동으로 계산되지 않습니다.
              </Text>
            </Flex>
          ),
        },
      ],
    },
    {
      label: '결제 경로',
      form: [
        {
          input: (
            <Radio.Group
              size="small"
              style={{ width: 300 }}
              value={inputValue?.order_route}
              options={Object.entries(ORDER_ROUTE).map(([key, value]) => ({
                label: value.title,
                value: key,
              }))}
              onChange={(e) => onChange('order_route', e.target.value)}
            />
          ),
        },
      ],
      required: true,
    },
    {
      label: '주문 타입',
      form: [
        {
          input: (
            <Radio.Group
              size="small"
              style={{ width: 300 }}
              value={inputValue?.type}
              options={[
                { label: '매장주문', value: 'GENERAL' },
                { label: '포장주문', value: 'TAKE_OUT' },
              ]}
              onChange={(e) => onChange('type', e.target.value)}
            />
          ),
        },
      ],
      required: true,
    },
    {
      label: '결제 수단',
      form: [
        {
          input: (
            <Flex gap={5} align="center">
              <Select
                size="middle"
                style={{ width: 300 }}
                value={inputValue?.payment_method}
                options={Object.entries(Payment['PAYMENT_METHOD']).map(
                  ([key, value]) => ({
                    label: value,
                    value: key,
                  })
                )}
                onChange={(e) => onChange('payment_method', e)}
              />
              <Text size={13} color={'#D33444'}>
                * 제이엠페이 선택 시 결제금액은 사용제이엠페이로 저장됩니다.
              </Text>
            </Flex>
          ),
        },
      ],
      required: true,
    },
    {
      label: '결제 시간',
      form: [
        {
          input: (
            <DatePicker
              size="large"
              showTime={true}
              style={{ width: '100%' }}
              value={
                inputValue.created_at ? dayjs(inputValue.created_at) : null
              }
              onChange={(e) => {
                onChange('created_at', e);
                // onChange('payment_completion_date', e);
              }}
              placeholder="날짜 선택"
              format="YYYY-MM-DD HH시 mm분 ss초"
            />
          ),
        },
      ],
      required: true,
    },
  ];
  //NOTE: 주문장 연결
  const form2 = [
    {
      label: '주문장 연결',
      form: [
        {
          input: (
            <Flex gap={10}>
              <Upload
                // onChange={handleFile}
                onRemove={(_file) => onUploadData(null)}
                beforeUpload={(file) => {
                  handleFile(file);
                  return false;
                }}
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>
                  엑셀파일을 양식에 맞추어 업로드해주세요
                </Button>
              </Upload>
              <Button onClick={() => handleGetExcelTemplate()}>
                양식 다운로드
              </Button>
              <Button
                type="primary"
                disabled={!uploadData}
                onClick={() => onSubmit('UPSERT')}
              >
                주문장 연결하기
              </Button>
            </Flex>
          ),
        },
      ],
    },
    {
      label: '처리결과',
      form: [
        {
          input: (
            <div
              style={{
                width: '100%',
                background: '#fafafa',
                border: '1px solid #f1f1f1',
                padding: 5,
                height: 120,
                overflowX: 'auto',
              }}
            >
              <ul>
                {uploadData && uploadData.length > 0 ? (
                  uploadData.map((item, key) => (
                    <li
                      key={`${item}_${key}`}
                      style={{
                        borderBottom: '1px solid #f1f1f1',
                        color: '#5a5a5a',
                        fontWeight: '300',
                      }}
                    >
                      {JSON.stringify(item)}
                    </li>
                  ))
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </ul>
            </div>
          ),
          fullscreen: true,
        },
      ],
    },
  ];
  //NOTE: 메뉴 선택 테이블
  const columns = {
    MENU: [
      {
        title: '메뉴명',
        key: 'menu_name',
        dataIndex: 'menu_name',
        width: 200,
        ...getColumnSearchProps('menu_name'),
      },
      {
        title: '카테고리명',
        key: 'product_category_name',
        dataIndex: 'product_category_name',
        width: 200,
        filters: categoryList.map((_item) => ({
          text: _item.product_category_name,
          value: _item.product_category_name,
        })),
        onFilter: (value, record) => record.product_category_name === value,
        render: (product_category_name) => product_category_name,
      },
      {
        title: '단가',
        key: 'price',
        dataIndex: 'price',
        width: 200,
        render: (price) => <>{stringToMoneyFormat(price)}원</>,
        sorter: (a, b) => a.price - b.price,
      },
      {
        title: '타입',
        key: 'type',
        dataIndex: 'type',
        width: 50,
        render: (type) => <>{MENU_TYPES[type]?.label}</>,
        filters: Object.entries(MENU_TYPES).map(([key, value]) => ({
          text: value.label,
          value: key,
        })),
        onFilter: (value, record) => record.type === value,
      },
    ],
    AMOUNT: [
      {
        title: '메뉴명',
        key: 'menu_name',
        dataIndex: 'menu_name',
        width: 200,
        // ...getColumnSearchProps('promotion_title'),
        sorter: (a, b) => a.menu_name.localeCompare(b.menu_name),
      },
      {
        title: '단가',
        key: 'price',
        dataIndex: 'price',
        width: 90,
        render: (price) => <>{stringToMoneyFormat(price)}원</>,
        sorter: (a, b) => a.price - b.price,
      },
      {
        title: '수량',
        key: 'num',
        dataIndex: 'num',
        width: 50,
        render: (_, record) => (
          <InputNumber
            size="medium"
            placeholder="숫자만 입력"
            style={{ ...styles.form.input.medium }}
            value={record.num}
            min={0}
            onChange={(e) => onAmountChange('num', e, record.key)}
          />
        ),
        sorter: (a, b) => a.num - b.num,
      },
      {
        title: '옵션',
        key: 'menu_option',
        dataIndex: 'menu_option',
        width: 350,
        render: (_, record) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {record.eared_temperature_option === 'ALL' ? (
              <div
                key={record.key}
                style={{
                  border: '1px solid #eee',
                  padding: 8,
                  borderRadius: 8,
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                  온도 옵션
                </div>
                <Radio.Group
                  value={record.temperature_option} // 현재 선택된 값
                  onChange={(e) =>
                    onAmountChange(
                      'temperature_option', // 변경할 필드명
                      e.target.value, // 선택된 값 ('HOT' or 'ICE')
                      record.key // 해당 항목의 key (주로 식별자)
                    )
                  }
                >
                  <Radio value={'HOT'}>HOT</Radio>
                  <Radio value={'ICE'}>ICE</Radio>
                </Radio.Group>
              </div>
            ) : null}

            {record.menu_option.map((opt) => {
              const isChoice = opt.option_type === 'CHOICE';
              const isCount = opt.option_type === 'COUNT';

              return (
                <div
                  key={opt.option_id}
                  style={{
                    border: '1px solid #eee',
                    padding: 8,
                    borderRadius: 8,
                  }}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                    {opt.option_title} (
                    {opt.type === 'DEFAULT' ? '기본' : '선택'} 옵션)
                  </div>

                  {isChoice && (
                    <>
                      <Checkbox
                        checked={opt.enabled ?? true}
                        disabled={opt.type === 'DEFAULT'}
                        onChange={(e) =>
                          onOptionToggle(
                            record.key,
                            opt.option_id,
                            e.target.checked
                          )
                        }
                      >
                        옵션 사용
                      </Checkbox>

                      {opt.enabled !== false && (
                        <Radio.Group
                          value={opt.option.find((o) => o.default)?.option_key}
                          onChange={(e) =>
                            onChoiceSelect(
                              record.key,
                              opt.option_id,
                              e.target.value
                            )
                          }
                        >
                          {opt.option.map((o) => (
                            <Radio key={o.option_key} value={o.option_key}>
                              {o.option_name} (
                              {stringToMoneyFormat(o.option_price)}원)
                            </Radio>
                          ))}
                        </Radio.Group>
                      )}
                    </>
                  )}

                  {isCount &&
                    opt.option.map((o) => (
                      <div
                        key={o.option_key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginTop: 4,
                        }}
                      >
                        <span>{o.option_name}</span>
                        <InputNumber
                          min={0}
                          value={o.count}
                          size="small"
                          style={{ width: 80 }}
                          onChange={(val) =>
                            onAmountChange(
                              'menu_option',
                              val,
                              record.key,
                              opt.option_id,
                              o.option_key
                            )
                          }
                        />
                        <span>
                          {o.option_price > 0 &&
                            `(+${stringToMoneyFormat(o.option_price)}원)`}
                        </span>
                      </div>
                    ))}
                </div>
              );
            })}
          </div>
        ),
      },
      {
        title: '#',
        key: 'key',
        dataIndex: 'key',
        width: 80,
        render: (key) => (
          <Flex gap={5}>
            <Button
              size="small"
              onClick={() => {
                onAmountList((prev) => [
                  ...prev,
                  {
                    ...amountList.find((item) => item.key === key),
                    key: Date.now(),
                  },
                ]);
              }}
            >
              복사하기
            </Button>
            <Button
              danger
              size="small"
              icon={<MinusOutlined />}
              onClick={() =>
                onAmountList((prev) => prev.filter((item) => item.key !== key))
              }
            />
          </Flex>
        ),
      },
    ],
  };

  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  return (
    <Spin spinning={isLoading}>
      <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
        <Row gutter={[16, 16]}>
          <Col x={24}>
            <Title style={{ ...styles.title }}>주문장 생성</Title>
          </Col>
          <Col x={24}>
            <Alert
              type="warning"
              showIcon
              message={<h4>주의!</h4>}
              description={
                <p>
                  주문장 생성 시 매출에 반영됩니다.
                  <br />
                  주문장이 누락되었을 경우 누락된 주문건에 대해서만
                  사용해주세요.
                </p>
              }
            />
          </Col>
          <Col x={24}>
            <Tabs
              type="card"
              size={'large'}
              defaultActiveKey="1"
              tabBarStyle={{ margin: 0 }}
              items={[
                {
                  label: `주문장 생성`,
                  key: '1',
                  children: (
                    <Content maxWidth={'100%'} padding={20}>
                      <Flex gap={24} vertical align="center">
                        <InputFormTable form={form} />
                        <Button
                          size="large"
                          type="primary"
                          onClick={() => onSubmit('CREATE')}
                        >
                          생성
                        </Button>
                      </Flex>
                    </Content>
                  ),
                },
                {
                  label: `매출 연결`,
                  key: '2',
                  children: (
                    <Content maxWidth={'100%'} padding={20}>
                      <Flex vertical gap={10}>
                        <Text size={15}>
                          * 등록 되어있는 매출과 회원을 연결하는 기능힙니다.
                        </Text>
                        <Text size={13} color={'#D33444'}>
                          * 엑셀 파일을 업로드하기 전에, 공백이 없는지와
                          연락처가 올바른 형식인지 확인해 주세요.
                        </Text>
                        <Text size={13} color={'#D33444'}>
                          * 엑셀 파일 내에 동일한 영수증 번호를 가진 회원
                          연락처가 여러 개 있을 경우, 먼저 입력된 항목만
                          연결됩니다.
                        </Text>
                        <InputFormTable form={form2} />
                      </Flex>
                    </Content>
                  ),
                },
              ]}
            />
          </Col>
        </Row>

        {/* 메뉴 선택 모달 시작 */}
        <ModalLayout
          type="modal"
          centered
          title={modalType === 'MENU' ? '메뉴 선택' : '금액 입력'}
          width={'80%'}
          open={isOpen}
          onCancel={() => {
            onIsOpen(false);
            onModalType('MENU');
          }}
          footer={null}
        >
          {modalType === 'MENU' ? (
            <Table
              key={`shop_${inputValue?.shop_information_id}`}
              rowKey={'menu_id'}
              dataSource={menuList}
              columns={columns[modalType]}
              rowSelection={{
                type: 'checkbox',
                onSelect: (record, selected) =>
                  onSelect(selected ? 'checked' : 'unchecked', record),
                onSelectAll: (selected, selectedRows) =>
                  onSelect(
                    selected ? 'all-checked' : 'all-unchecked',
                    selectedRows
                  ),
                selectedRowKeys: selectedList?.map((area) => area?.menu_id),
              }}
            />
          ) : modalType === 'AMOUNT' ? (
            <Table dataSource={amountList} columns={columns[modalType]} />
          ) : null}
          <Flex justify="flex-end" gap={5} style={{ marginTop: 10 }}>
            {modalType === 'AMOUNT' ? (
              <>
                <Button
                  type="default"
                  onClick={() => {
                    onAmountList([]);
                    onModalType('MENU');
                  }}
                >
                  이전
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    onMenuComplete();
                  }}
                >
                  완료
                </Button>
              </>
            ) : (
              <Button
                disabled={selectedList.length === 0}
                type="default"
                onClick={() => {
                  onModalType('AMOUNT');
                  onAmountList(selectedList);
                }}
              >
                다음
              </Button>
            )}
          </Flex>
        </ModalLayout>
        {/* 메뉴 선택 모달 끝 */}
      </Content>
    </Spin>
  );
};

export default SaleRegisterPresenter;
